import { useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { TextLookupSelection } from '../data/vocabularyLookup'

interface ButtonPosition {
  left: number
  top: number
  placement: 'above' | 'below'
}

const viewportBounds = () => {
  const viewport = window.visualViewport
  return {
    left: viewport?.pageLeft ?? window.scrollX,
    top: viewport?.pageTop ?? window.scrollY,
    width: viewport?.width ?? window.innerWidth,
    height: viewport?.height ?? window.innerHeight,
  }
}

export function SelectionLookupButton({ selection, onLookup }: { selection?: TextLookupSelection; onLookup: () => void }) {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [position, setPosition] = useState<ButtonPosition>()

  useLayoutEffect(() => {
    const button = buttonRef.current
    if (!selection || !button) {
      setPosition(undefined)
      return
    }
    const viewport = viewportBounds()
    const buttonRect = button.getBoundingClientRect()
    const margin = 8
    const gap = 10
    const viewportRight = viewport.left + viewport.width
    const viewportBottom = viewport.top + viewport.height
    const left = Math.max(
      viewport.left + margin + buttonRect.width / 2,
      Math.min(viewportRight - margin - buttonRect.width / 2, selection.anchor.left),
    )
    const belowTop = selection.anchor.bottom + gap
    const aboveTop = selection.anchor.top - gap - buttonRect.height
    const hasRoomBelow = belowTop + buttonRect.height <= viewportBottom - margin
    const hasRoomAbove = aboveTop >= viewport.top + margin
    const placement = hasRoomBelow || !hasRoomAbove ? 'below' : 'above'
    const preferredTop = placement === 'below' ? belowTop : aboveTop
    const top = Math.max(viewport.top + margin, Math.min(viewportBottom - margin - buttonRect.height, preferredTop))
    setPosition({ left, top, placement })
  }, [selection])

  if (!selection || typeof document === 'undefined') return null
  return createPortal(
    <button
      ref={buttonRef}
      type="button"
      className={`selection-lookup-button selection-lookup-button--${position?.placement ?? 'below'}`}
      style={{ left: position?.left ?? 0, top: position?.top ?? 0, visibility: position ? 'visible' : 'hidden' }}
      onPointerDown={(event) => {
        event.preventDefault()
        onLookup()
      }}
      onClick={(event) => {
        if (event.detail === 0) onLookup()
      }}
      aria-label={`查释义：${selection.term}`}
    >
      查释义
    </button>,
    document.body,
  )
}
